// PlaybackControls Component v1.0.0 - Transport controls
import { useCallback } from 'react'
import { format } from 'date-fns'
import { useViewerStore } from '../stores/viewerStore'

interface PlaybackControlsProps {
  onExport?: () => void
}

export default function PlaybackControls({ onExport }: PlaybackControlsProps) {
  const {
    isPlaying,
    togglePlayback,
    playbackSpeed,
    setPlaybackSpeed,
    currentTime,
    setCurrentTime,
    goToTime,
  } = useViewerStore()

  // Skip backward/forward
  const skip = useCallback(
    (seconds: number) => {
      const newTime = new Date(currentTime.getTime() + seconds * 1000)
      setCurrentTime(newTime)
      goToTime(newTime)
    },
    [currentTime, setCurrentTime, goToTime]
  )

  // Go to live (now)
  const goToLive = useCallback(() => {
    const now = new Date()
    setCurrentTime(now)
    goToTime(now)
  }, [setCurrentTime, goToTime])

  // Keyboard shortcuts
  // (Main shortcuts are in Timeline, these are additional)

  return (
    <div className="flex items-center justify-between px-4 py-2 bg-viewer-surface border-t border-viewer-border">
      {/* Left: Transport controls */}
      <div className="flex items-center gap-2">
        {/* Skip back 60s */}
        <button
          onClick={() => skip(-60)}
          className="p-2 rounded hover:bg-viewer-surface-hover"
          title="Skip back 60s"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0019 16V8a1 1 0 00-1.6-.8l-5.333 4zM4.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0011 16V8a1 1 0 00-1.6-.8l-5.334 4z" />
          </svg>
        </button>

        {/* Skip back 10s */}
        <button
          onClick={() => skip(-10)}
          className="p-2 rounded hover:bg-viewer-surface-hover"
          title="Skip back 10s"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>

        {/* Play/Pause */}
        <button
          onClick={togglePlayback}
          className="p-3 rounded-full bg-viewer-accent hover:bg-viewer-accent-hover text-white"
          title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
        >
          {isPlaying ? (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="4" width="4" height="16" />
              <rect x="14" y="4" width="4" height="16" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        {/* Skip forward 10s */}
        <button
          onClick={() => skip(10)}
          className="p-2 rounded hover:bg-viewer-surface-hover"
          title="Skip forward 10s"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
        </button>

        {/* Skip forward 60s */}
        <button
          onClick={() => skip(60)}
          className="p-2 rounded hover:bg-viewer-surface-hover"
          title="Skip forward 60s"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.933 12.8a1 1 0 000-1.6L6.6 7.2A1 1 0 005 8v8a1 1 0 001.6.8l5.333-4zM19.933 12.8a1 1 0 000-1.6l-5.333-4A1 1 0 0013 8v8a1 1 0 001.6.8l5.333-4z" />
          </svg>
        </button>
      </div>

      {/* Center: Current time display */}
      <div className="flex items-center gap-4">
        <div className="text-center">
          <div className="text-2xl font-mono font-bold">{format(currentTime, 'HH:mm:ss')}</div>
          <div className="text-xs text-viewer-text-secondary">{format(currentTime, 'EEEE, MMMM d, yyyy')}</div>
        </div>
      </div>

      {/* Right: Speed & actions */}
      <div className="flex items-center gap-4">
        {/* Playback speed */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-viewer-text-secondary">Speed:</span>
          <select
            value={playbackSpeed}
            onChange={(e) => setPlaybackSpeed(Number(e.target.value))}
            className="bg-viewer-bg border border-viewer-border rounded px-2 py-1 text-sm"
          >
            <option value={0.25}>0.25x</option>
            <option value={0.5}>0.5x</option>
            <option value={1}>1x</option>
            <option value={2}>2x</option>
            <option value={4}>4x</option>
            <option value={8}>8x</option>
            <option value={16}>16x</option>
          </select>
        </div>

        {/* Go to live */}
        <button
          onClick={goToLive}
          className="flex items-center gap-1 px-3 py-1.5 rounded bg-viewer-danger text-white text-sm hover:bg-red-600"
          title="Go to live"
        >
          <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
          LIVE
        </button>

        {/* Export */}
        {onExport && (
          <button
            onClick={onExport}
            className="flex items-center gap-1 px-3 py-1.5 rounded bg-viewer-surface-hover hover:bg-viewer-border text-sm"
            title="Export clip"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Export
          </button>
        )}
      </div>
    </div>
  )
}
